import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { from } from 'rxjs/internal/observable/from';
import * as transactions from './transactions.actions';
import {
  TransactionsData,
  TransactionsDetailsRequestPayload,
  TransactionsDetailsResponsePayload,
  TransactionsExportRequestPayload,
  TransactionsRequestPayload
} from './transactions.models';
import { HttpService } from '../../global/http.service';
import { amountOfTransactionsForPDFExport, createPostQuery } from '../../shared/utils';

import { HttpResponse } from '@angular/common/http';
import { downloadFile } from '../../global/fileDownload';
import { DatesInterval, DatesIntervalRequest } from '../../global/interfaces';
import { isNullOrUndefined } from 'util';
import { Actions as ActionsCDK } from "atlas-cdk";
import { ToastService } from '../../shared/toaster_/toaster_.service';

@Injectable()
export class TransactionsEffects {
  public loadTransactionsDates$ = createEffect(() => this.actions$.pipe(
    ofType<transactions.LoadTransactionsDatesAction>(transactions.ActionTypes.LOAD_TRANSACTIONS_DATES),
    map(action => action.payload),
    switchMap((payload: DatesIntervalRequest) =>
      this.loadTransactionsDates(payload).pipe(
        map((response: DatesInterval) =>
          new transactions.LoadTransactionsDatesSuccessAction(response)
        ),
        catchError(error =>
          // from([
          //   new transactions.LoadTransactionsDatesFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          // {
          //   this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
          //   return of(new transactions.LoadTransactionsDatesFailedAction(error));
          // }
          concat(
            of(new transactions.LoadTransactionsDatesFailedAction(error)),
            of(null).pipe(
              tap(() => {
                this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public loadTransactions$ = createEffect(() => this.actions$.pipe(
    ofType<transactions.LoadTransactionsAction>(transactions.ActionTypes.LOAD_TRANSACTIONS),
    map(action => action.payload),
    switchMap(payload =>
      this.loadTransactions(payload).pipe(
        map((response: TransactionsData[]) =>
          new transactions.LoadTransactionsSuccessAction(response)
        ),
        catchError(error =>
          // from([
          //   new transactions.LoadTransactionsFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          // {
          //   this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
          //   return of(new transactions.LoadTransactionsFailedAction(error));
          // }
          concat(
            of(new transactions.LoadTransactionsFailedAction(error)),
            of(null).pipe(
              tap(() => {
                this._toasterService.launchAlertError(error.error.message, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public loadTransactionsForPDF$ = createEffect(() => this.actions$.pipe(
    ofType<transactions.LoadTransactionsForPDFAction>(transactions.ActionTypes.LOAD_TRANSACTIONS_FOR_PDF),
    map(action => action.payload),
    switchMap(payload =>
      this.loadTransactions(payload, amountOfTransactionsForPDFExport).pipe(
        map((response: TransactionsData[]) =>
          new transactions.LoadTransactionsForPDFSuccessAction(response)
        ),
        catchError(error =>
        (from([
          new transactions.LoadTransactionsForPDFFailedAction(error),
        ])
        ))
      )
    )
  ));

  public loadTransactionsDetails$ = createEffect(() => this.actions$.pipe(
    ofType<transactions.LoadTransactionsDetailsAction>(transactions.ActionTypes.LOAD_TRANSACTIONS_DETAILS),
    map(action => action.payload),
    switchMap(payload =>
      this.loadTransactionsDetails(payload).pipe(
        map((response: TransactionsDetailsResponsePayload) =>
          new transactions.LoadTransactionsDetailsSuccessAction(response)
        ),
        catchError(error =>
          // from([
          //   new transactions.LoadTransactionsDetailsFailedAction(error),
          //   new AddErrorToastAction(!isNullOrUndefined(error.error)
          //     && !isNullOrUndefined(error.error.message) ? error.error.message : error.message),
          // ])
          // {
          //   this._toasterService.launchAlertError(error.error != null && error.error.message != null ? error.error.message : error.message, ActionsCDK.error);
          //   return of(new transactions.LoadTransactionsDetailsFailedAction(error));
          // }
          concat(
            of(new transactions.LoadTransactionsDetailsFailedAction(error)),
            of(null).pipe(
              tap(() => {
                const errorMessage = (error?.error != null && error.error.message != null)
                  ? error.error.message
                  : error.message;
                this._toasterService.launchAlertError(errorMessage, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  /**
   * Uses same endpoint as details to load data for mobile version
   */
  public loadTransactionsMobile$ = createEffect(() => this.actions$.pipe(
    ofType<transactions.LoadTransactionsMobileAction>(transactions.ActionTypes.LOAD_TRANSACTIONS_MOBILE),
    map(action => action.payload),
    switchMap(payload =>
      this.loadTransactionsDetails(payload).pipe(
        map((response: TransactionsDetailsResponsePayload) =>
          new transactions.LoadTransactionsMobileSuccessAction(response)
        ),
        catchError(error =>
          // from([
          //   new transactions.LoadTransactionsMobileFailedAction(error),
          //   new AddErrorToastAction(error.error.message)
          // ])
          // {
          //   this._toasterService.launchAlertError(error.error != null && error.error.message != null ? error.error.message : error.message, ActionsCDK.error);
          //   return of(new transactions.LoadTransactionsMobileFailedAction(error));
          // }
          concat(
            of(new transactions.LoadTransactionsMobileFailedAction(error)),
            of(null).pipe(
              tap(() => {
                const errorMessage = (error?.error != null && error.error.message != null)
                  ? error.error.message
                  : error.message;
                this._toasterService.launchAlertError(errorMessage, ActionsCDK.error);
              })
            )
          )
        )
      )
    )
  ));

  public transactionsExport$ = createEffect(() => this.actions$.pipe(
    ofType<transactions.ExportTransactionsReportAction>(transactions.ActionTypes.EXPORT_TRANSACTIONS_REPORT),
    map(action => action.payload),
    switchMap(payload =>
      this.downloadTransactionsReport(payload).pipe(
        map(response => {
          // deepcode ignore DOMXSS:internally created secure response
          downloadFile(response.body, 'text/csv', 'TransactionsExport.csv');
        }),
        catchError(() => {
          //[new AddErrorToastAction('Download failed. Please try again later.')]
          this._toasterService.launchAlertError('Download failed. Please try again later.', ActionsCDK.error);
          return EMPTY;
        }
        )
      )
    )
  ), { dispatch: false });

  public $exportTransactionsDetails = createEffect(() => this.actions$.pipe(
    ofType<transactions.ExportTransactionsDetailsAction>(transactions.ActionTypes.EXPORT_TRANSACTIONS_DETAILS),
    map(action => action.payload),
    switchMap(payload =>
      this.downloadTransactionsDetailsReport(payload).pipe(
        map(response => {
          // deepcode ignore DOMXSS:internally created secure response
          downloadFile(response.body, 'application/pdf', 'TransactionsDetailsExport.pdf');
        }),
        catchError(() => {
          // [new AddErrorToastAction('Download failed. Please try again later.')]

          this._toasterService.launchAlertError('Download failed. Please try again later.', ActionsCDK.error);
          return EMPTY;
        }
        )
      )
    )
  ), { dispatch: false });

  public constructor(private readonly actions$: Actions, private readonly http: HttpService, private readonly _toasterService: ToastService) {
  }

  private loadTransactionsDates(payload: DatesIntervalRequest): Observable<DatesInterval> {
    return this.http.get(`/metrics/${payload.account}/date-ranges/transactions`);
  }

  private loadTransactions(payload: TransactionsRequestPayload, size?: string): Observable<TransactionsData[]> {
    payload.sort = 'date';
    payload.direction = 'asc';
    payload.size = size;
    return this.http.post(`/transactions/accounts/${payload.account}`, payload);
  }

  private loadTransactionsDetails(payload: TransactionsDetailsRequestPayload): Observable<TransactionsDetailsResponsePayload> {
    return this.http.post(`/transactions/accounts/${payload.account}`, createPostQuery(payload.params));
  }

  private downloadTransactionsReport(payload: TransactionsExportRequestPayload): Observable<HttpResponse<Blob>> {
    payload.type = 'csv';
    const url = `/transactions/accounts/${payload.account}/export`;
    return this.http.post(url, payload, { observe: 'response', responseType: 'blob' });
  }

  private downloadTransactionsDetailsReport(payload: TransactionsExportRequestPayload): Observable<HttpResponse<Blob>> {
    payload.type = 'csv';
    const url = `/transactions/accounts/${payload.account}/export-details`;
    return this.http.post(url, payload, { observe: 'response', responseType: 'blob' });
  }
}
